import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Switch,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatDate } from '../../../utils/dateFormat';
import type { DomainAutoRenew, DomainName } from '../../../types/models/DomainName';
import type { ApiResponseDomainAutoRenew } from '../../../types/api/ApiResponseDomainName';
import { AUTO_RENEW_STATE_TEXT, autoRenewControl, autoRenewState, isLapsedForNonpayment } from './autoRenew';

/** Compact on / off / unknown badge for the domain list — or the lapsed-for-non-payment marker. */
export function AutoRenewLabel({ autoRenew }: { autoRenew: DomainAutoRenew }) {
  if (isLapsedForNonpayment(autoRenew)) {
    return (
      <Label color="red" isCompact>
        Lapsed — unpaid renewal
      </Label>
    );
  }
  const state = autoRenewState(autoRenew);
  if (state === 'unknown') {
    return (
      <Label variant="outline" isCompact>
        {AUTO_RENEW_STATE_TEXT.unknown}
      </Label>
    );
  }
  return (
    <Label color={state === 'on' ? 'green' : 'orange'} isCompact>
      {AUTO_RENEW_STATE_TEXT[state]}
    </Label>
  );
}

/** "will expire on 15 janvier 2026" — or the term-end wording when no expiry is on record. */
function expiryPhrase(expiresAt: string | null): string {
  return expiresAt === null ? 'will expire at the end of its current term' : `will expire on ${formatDate(expiresAt)}`;
}

interface DomainAutoRenewFieldProps {
  domainName: DomainName;
  autoRenew: DomainAutoRenew;
}

/**
 * The registrar auto-renew switch on the domain detail page. Turning it on is immediate; turning it
 * off asks for confirmation first, because the domain then lapses at expiry and its renewal is not
 * invoiced. A successful PUT's refreshed block is applied over the loaded entity (a refetch of the
 * domain returns the same value), so no reload is needed.
 */
function DomainAutoRenewField({ domainName, autoRenew: loadedAutoRenew }: DomainAutoRenewFieldProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [override, setOverride] = useState<DomainAutoRenew | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // A freshly loaded domain is authoritative — drop the last PUT's block once the parent refetches.
  useEffect(() => {
    setOverride(null);
  }, [loadedAutoRenew]);

  const autoRenew = override ?? loadedAutoRenew;
  const control = autoRenewControl(autoRenew, domainName.in_transfer);
  const domainId = domainName.id;
  const hintId = `auto-renew-hint-${domainId ?? domainName.name}`;

  const submit = useCallback(
    async (enabled: boolean) => {
      if (domainId === null) return;
      setIsSubmitting(true);
      try {
        const response = await apiFetch(`/datacenters/domain-names/${domainId}/auto-renew`, {
          method: 'PUT',
          body: JSON.stringify({ enabled }),
        });
        // JSend: failures arrive as status:'error' at HTTP 200 with a display-safe message.
        const body = (await response.json().catch(() => null)) as ApiResponseDomainAutoRenew | null;
        if (body?.status !== 'success' || !body.data?.auto_renew) {
          throw new Error(body?.message ?? `The request failed (HTTP ${response.status}).`);
        }

        setOverride(body.data.auto_renew);
        addNotification({
          title: enabled ? 'Auto-renew turned on' : 'Auto-renew turned off',
          srTitle: 'Success alert',
          description: enabled
            ? `${domainName.name} will be renewed at expiry.`
            : `${domainName.name} will not be renewed and ${expiryPhrase(domainName.expires_at)}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not update auto-renew',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setIsSubmitting(false);
        setIsConfirmOpen(false);
      }
    },
    [apiFetch, addNotification, setDrawerOpen, domainId, domainName.name, domainName.expires_at],
  );

  const handleChange = (checked: boolean) => {
    if (checked) {
      void submit(true);
    } else {
      setIsConfirmOpen(true);
    }
  };

  return (
    <>
      <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
        <FlexItem>
          <Switch
            id={`auto-renew-${domainId ?? domainName.name}`}
            aria-label="Renew automatically at expiry"
            aria-describedby={control.hint !== null ? hintId : undefined}
            isChecked={control.isChecked}
            isDisabled={control.isDisabled || isSubmitting || domainId === null}
            hasCheckIcon
            onChange={(_event, checked) => handleChange(checked)}
          />
        </FlexItem>
        <FlexItem>{AUTO_RENEW_STATE_TEXT[control.state]}</FlexItem>
        {isSubmitting && (
          <FlexItem>
            <Spinner size="sm" aria-label="Updating auto-renew" />
          </FlexItem>
        )}
      </Flex>
      {control.hint !== null && (
        <Content component="small" id={hintId} className="pf-v6-u-display-block pf-v6-u-mt-xs">
          {control.hint}
          {control.showInvoicesLink && (
            <>
              {' '}
              <Link to="/organization/invoices">Go to invoices</Link>
            </>
          )}
        </Content>
      )}

      <Modal
        variant="small"
        isOpen={isConfirmOpen}
        onClose={() => !isSubmitting && setIsConfirmOpen(false)}
        aria-labelledby="auto-renew-off-title"
      >
        <ModalHeader title="Turn off auto-renew" labelId="auto-renew-off-title" titleIconVariant="warning" />
        <ModalBody>
          {autoRenew.renewal_invoiced && (
            <Alert variant="warning" isInline title="This term's renewal is already invoiced" className="pf-v6-u-mb-md">
              Turning auto-renew off does not refund it — issue the customer a credit note by hand.
            </Alert>
          )}
          <Content>
            <strong>{domainName.name}</strong> will not be renewed and {expiryPhrase(domainName.expires_at)}. The
            renewal will not be invoiced.
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="danger"
            onClick={() => void submit(false)}
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
          >
            Turn off
          </Button>
          <Button variant="link" onClick={() => setIsConfirmOpen(false)} isDisabled={isSubmitting}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default DomainAutoRenewField;
